//
// # Instance Context Menu
//
// Displays instance information and actions when right-clicking on layout instances.
// Selection-aware: operates on the full selection, not just the right-clicked instance.

import type { Editor } from "./editor";
import {
  InstanceEntity,
  ScalarInstanceEntity,
  Segment,
} from "DrawCore/src/content/layout/instance";
import { RouteEntity } from "DrawCore/src/content/layout/route";
import { LayoutContent } from "EditorCore/src/content/layout/content";
import * as changes from "DrawCore/src/changes";
import type { schema } from "GenAlphaSchema";
import { layout } from "GenAlphaModel";
import { InstanceBaseContextMenu } from "./instanceBaseContextMenu";
import { escapeHtml } from "./baseContextMenu";
import { Palette } from "DrawCore/src/style/palette";

export class InstanceContextMenu extends InstanceBaseContextMenu {
  // The specific instance under the mouse pointer (for single-instance info display)
  private _currentInstance: ScalarInstanceEntity | Segment | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  // Show context menu for an instance at the given position.
  // `selectionSize` indicates how many items are currently selected (for multi-select UI).
  show = async (
    instance: ScalarInstanceEntity | Segment,
    pageX: number,
    pageY: number,
    selectionSize: number = 1
  ): Promise<void> => {
    // Close any other open context menus first
    this.editor.hideAllContextMenus();

    this._currentInstance = instance;
    this.selectionSize = selectionSize;

    // Get instance data
    const instanceData =
      instance instanceof ScalarInstanceEntity
        ? instance.data
        : instance.scalarData;

    // Create or update menu element
    this.ensureMenuElement();

    // Build menu content
    const content = this.buildMenuContent(instanceData);

    this.displayMenu(content, pageX, pageY);

    // Set up click handlers for menu items
    this.setupInstanceMenuItemHandlers();
    // Wire up segment-specific action handlers
    this.setupSegmentMenuItemHandlers();
  };

  // Build the HTML content for the menu
  private buildMenuContent = (instanceData: unknown): string => {
    // Multi-select: show simplified header and only bulk actions
    if (this.selectionSize > 1) {
      return this.buildMultiSelectContent();
    }

    return this.buildSingleInstanceContent(instanceData);
  };

  // Build content for single-instance case (original behavior)
  private buildSingleInstanceContent = (instanceData: unknown): string => {
    // Type guard: instanceData is ScalarInstance from GenAlphaModel (layout.Instance with resolved target)
    // We use unknown and check structure at runtime
    if (
      typeof instanceData !== "object" ||
      instanceData === null ||
      !("name" in instanceData) ||
      !("target" in instanceData)
    ) {
      return "<div>Invalid instance data</div>";
    }

    const data = instanceData as {
      name: string;
      target: unknown;
      params?: Record<string, unknown>;
      id?: number;
      place?: unknown;
    };

    const name = data.name;
    const target = data.target;
    const params = data.params;
    const instanceId = data.id;

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header">Instance: ${escapeHtml(
      name
    )}</div>`;

    // Helper to extract clean generator name from path
    const getGeneratorDisplayName = (path: string[]): string => {
      // Path format: ["generated", "generators", pdkId, generatorName, hash]
      // or: ["generators", pdkId, generatorName]
      if (
        path[0] === "generated" &&
        path[1] === "generators" &&
        path.length >= 4
      ) {
        return `${path[2]}.${path[3]}`; // pdkId.generatorName
      } else if (path[0] === "generators" && path.length >= 3) {
        return `${path[1]}.${path[2]}`; // pdkId.generatorName
      }
      return path.join(".");
    };

    // Check if this is a generator instance (for determining edit button)
    let targetIdent: schema.Ident | null = null;
    let isGeneratorInstance = false;

    // Show cell name - target is BoundTarget for ScalarInstance
    if (
      typeof target === "object" &&
      target !== null &&
      "kind" in target &&
      target.kind === "bound" &&
      "layout" in target &&
      typeof target.layout === "object" &&
      target.layout !== null &&
      "ident" in target.layout
    ) {
      targetIdent = (target.layout as { ident: schema.Ident }).ident;
      if (targetIdent.kind === "libcellview") {
        content += `<div class="context-menu-target">Cell: ${targetIdent.lib}.${targetIdent.cell}</div>`;
      } else if (targetIdent.kind === "hierpath") {
        // Check if it's a generator instance
        isGeneratorInstance = Boolean(
          (targetIdent.path[0] === "generators" ||
            targetIdent.path[1] === "generators") &&
            params &&
            Object.keys(params).length > 0
        );

        if (isGeneratorInstance) {
          const generatorDisplayName = getGeneratorDisplayName(
            targetIdent.path
          );
          content += `<div class="context-menu-target">Cell: ${generatorDisplayName}</div>`;
        } else {
          content += `<div class="context-menu-target">Cell: ${targetIdent.path.join(
            "."
          )}</div>`;
        }
      } else {
        content += `<div class="context-menu-target">Cell: ${JSON.stringify(
          targetIdent
        )}</div>`;
      }
    } else if (
      typeof target === "object" &&
      target !== null &&
      "ident" in target
    ) {
      targetIdent = (target as { ident: schema.Ident }).ident;
      if (targetIdent.kind === "libcellview") {
        content += `<div class="context-menu-target">Target: ${targetIdent.lib}.${targetIdent.cell} (unbound)</div>`;
      } else if (targetIdent.kind === "hierpath") {
        // Check if it's a generator instance
        isGeneratorInstance = Boolean(
          targetIdent.path[0] === "generators" &&
            params &&
            Object.keys(params).length > 0
        );

        if (isGeneratorInstance) {
          const generatorDisplayName = getGeneratorDisplayName(
            targetIdent.path
          );
          content += `<div class="context-menu-target">Cell: ${generatorDisplayName} (unbound)</div>`;
        } else {
          content += `<div class="context-menu-target">Target: ${targetIdent.path.join(
            "."
          )} (unbound)</div>`;
        }
      } else {
        content += `<div class="context-menu-target">Target: ${JSON.stringify(
          targetIdent
        )} (unbound)</div>`;
      }
    }

    // Show position if available
    if (
      data.place &&
      typeof data.place === "object" &&
      "kind" in data.place &&
      data.place.kind === "scalar" &&
      "xform" in data.place &&
      typeof data.place.xform === "object" &&
      data.place.xform !== null &&
      "loc" in data.place.xform
    ) {
      const loc = (data.place.xform as { loc: { x: number; y: number } }).loc;
      content += `<div class="context-menu-position">Position: (${loc.x.toFixed(
        1
      )}, ${loc.y.toFixed(1)})</div>`;
    }

    // Show the place kind (scalar, segments, or array) so users can
    // identify the underlying data format — useful for debugging
    // array-vs-segments discrepancies after save/reload.
    if (this._currentInstance) {
      let placeKind: string | null = null;
      if (this._currentInstance instanceof Segment) {
        // For segments, resolve to the InstanceEntity to get the real
        // place kind (the drawer always reports "segments" even for "array").
        const layoutContent = this._currentInstance.parent.root;
        if (layoutContent instanceof LayoutContent) {
          const instanceEntity = layoutContent.resolveChild(
            this._currentInstance
          );
          if (instanceEntity instanceof InstanceEntity) {
            placeKind = instanceEntity.inner.kind;
          }
        }
      } else if (
        data.place &&
        typeof data.place === "object" &&
        "kind" in data.place
      ) {
        placeKind = (data.place as { kind: string }).kind;
      }
      if (placeKind) {
        content += `<div class="context-menu-position">Kind: ${escapeHtml(
          placeKind
        )}</div>`;
      }
    }

    // Show generator parameters if this is a parametrized instance

    if (isGeneratorInstance) {
      content += `<div class="context-menu-section">`;
      content += `<div class="context-menu-section-header">Parameters:</div>`;
      Object.entries(params as Record<string, unknown>).forEach(
        ([key, value]) => {
          content += `<div class="context-menu-param">  ${key}: ${value}</div>`;
        }
      );
      content += `</div>`;
    }

    content += `</div>`; // Close info section

    // Store the target ident for use in handlers
    this.currentTargetIdent = targetIdent;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Add action items
    content += `<div class="context-menu-actions">`;

    // Add "Open in New Tab" option if we have a valid target
    if (targetIdent) {
      // OpenAccess designs (libcellview idents) live in the OpenAccess tree, not
      // the Depot — label the reveal action accordingly.
      const viewInLabel =
        targetIdent.kind === "libcellview"
          ? "View in OpenAccess"
          : "View in Depot";
      content += `<div class="context-menu-item" data-action="open-target">Open in New Tab</div>`;
      content += `<div class="context-menu-item" data-action="view-in-depot">${viewInLabel}</div>`;
    }

    if (isGeneratorInstance && instanceId !== undefined) {
      content += `<div class="context-menu-item" data-action="edit-params" data-instance-id="${instanceId}">Edit Parameters...</div>`;
      content += `<div class="context-menu-item" data-action="add-dummy-devices" data-instance-id="${instanceId}" data-instance-name="${escapeHtml(
        name
      )}">Add Dummy Devices...</div>`;
    }

    if (instanceId !== undefined) {
      content += `<div class="context-menu-item" data-action="rename" data-instance-id="${instanceId}">Rename...</div>`;
    }

    content += `<div class="context-menu-item" data-action="duplicate">Duplicate</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete</div>`;

    // Show in Hierarchy
    if (instanceId !== undefined) {
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="show-in-hierarchy" data-instance-name="${escapeHtml(
        name
      )}" data-instance-id="${instanceId}">Show in Hierarchy</div>`;
    }

    // Segment-specific actions for multi-segment generators
    if (this._currentInstance instanceof Segment) {
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="select-all-segments">Select All Segments</div>`;
      content += `<div class="context-menu-item" data-action="reset-segment-positions">Reset Segment Positions</div>`;
    }

    content += `</div>`;

    return content;
  };

  // Wire up segment-specific action handlers (called after base handler setup).
  private setupSegmentMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const segItems = this.menuElement.querySelectorAll(
      '[data-action="select-all-segments"], [data-action="reset-segment-positions"]'
    );
    segItems.forEach((item) => {
      const el = item as HTMLElement;
      el.onclick = (e) => {
        e.stopPropagation();
        const action = el.getAttribute("data-action");
        if (action === "select-all-segments") {
          this.handleSelectAllSegments();
        } else if (action === "reset-segment-positions") {
          this.handleResetSegmentPositions();
        }
        this.hide();
      };
    });
  };

  // Select all segments of the current instance's parent generator.
  private handleSelectAllSegments = (): void => {
    const inst = this._currentInstance;
    if (!(inst instanceof Segment)) return;
    const content = this.editor.content;
    if (!(content instanceof LayoutContent)) return;
    const parent = content.resolveChild(inst);
    if (parent) {
      content.selectAllSegmentsOf(parent as InstanceEntity);
    }
  };

  // Reset all segments of the current instance's parent to their original positions.
  private handleResetSegmentPositions = (): void => {
    const inst = this._currentInstance;
    if (!(inst instanceof Segment)) return;
    const content = this.editor.content;
    if (!(content instanceof LayoutContent)) return;
    const parent = content.resolveChild(inst);
    if (!parent) return;

    // Safety: parent is an InstanceEntity with segmented inner.
    // Use the typed `inner` discriminated union for safe narrowing.
    const instanceEntity = parent as InstanceEntity;
    const { inner } = instanceEntity;
    if (inner.kind !== "segments" && inner.kind !== "array") return;
    // After narrowing, inner.drawer is SegmentedInstanceEntity with typed
    // data.place.segments (Array<schema.Segment>).
    const { segments } = inner.drawer.data.place;

    // Snapshot current positions for undo
    const origSegments = segments.map((seg) =>
      seg ? { ...seg, loc: { ...seg.loc } } : null
    );

    // Reset each segment to loc (0, 0) with default spacing.
    // Use a simple horizontal arrangement with the first segment's x as base.
    const firstSeg = segments.find((s) => s !== null);
    if (!firstSeg) return;

    // Compute default spacing from the original first two segments
    let defaultSpacing = 0;
    if (origSegments.length >= 2) {
      const s0 = origSegments[0];
      const s1 = origSegments[1];
      if (s0 && s1) {
        defaultSpacing = s1.loc.x - s0.loc.x;
      }
    }
    // If spacing is zero or negative, use a reasonable default
    // Default segment spacing in layout units (1 micron)
    const DEFAULT_SEGMENT_SPACING = 1000;
    if (defaultSpacing <= 0) defaultSpacing = DEFAULT_SEGMENT_SPACING;

    const baseLoc = { x: firstSeg.loc.x, y: firstSeg.loc.y };
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg) {
        seg.loc = { x: baseLoc.x + i * defaultSpacing, y: baseLoc.y };
      }
    }
    instanceEntity.update(instanceEntity.data);

    // Log the change for undo/redo and server sync.
    content.logChange(
      changes.update({
        target: instanceEntity,
        from: {
          place: {
            kind: "segments" as const,
            segments: origSegments,
          },
        },
        to: {
          place: {
            kind: "segments" as const,
            segments: segments.map((seg) =>
              seg ? { ...seg, loc: { ...seg.loc } } : null
            ),
          },
        },
      })
    );
    this.editor.requestRedraw();
  };

  // Null out instance-specific state (called by base clearState)
  protected clearInstanceState(): void {
    this._currentInstance = null;
  }
}

//
// # Route Context Menu
//
// Displays route information and actions when right-clicking on a layout route.
//
export class RouteContextMenu {
  private menuElement: HTMLDivElement | null = null;
  private currentRoute: RouteEntity | null = null;
  // Segment index of the right-clicked segment, or null if unresolved.
  private currentSegIdx: number | null = null;
  private styleSheet: HTMLStyleElement | null = null;

  constructor(private readonly editor: Editor) {
    this.setupGlobalClickHandler();
  }

  show = (
    route: RouteEntity,
    pageX: number,
    pageY: number,
    segIdx?: number | null
  ): void => {
    this.currentRoute = route;
    this.currentSegIdx = segIdx ?? null;

    if (!this.menuElement) {
      this.menuElement = document.createElement("div");
      this.setupMenuStyles();
      document.body.appendChild(this.menuElement);
    }

    const net = route.data.net ? route.data.net : "(unnamed)";

    // Build layer items for the "Change Layer" submenu
    const content = this.editor.content;
    let layerItemsHtml = "";
    if (content instanceof LayoutContent && segIdx != null) {
      const metals = content.stack?.metalLayers;
      const currentLayer = route.data.path[segIdx]?.layer;
      if (metals && currentLayer) {
        for (let i = 0; i < metals.length; i++) {
          const m = metals[i];
          const label = `M${i + 1}`;
          const dir = m.direction ?? "";
          const active =
            m.layer === currentLayer.layer &&
            m.purpose === currentLayer.purpose;
          const check = active ? ">> " : "";
          layerItemsHtml += `<div class="route-menu-item route-layer-item" data-layer-idx="${i}">${check}${label} (${dir})</div>`;
        }
      }
    }

    this.menuElement.innerHTML = `
      <div class="route-menu-info">
        <div class="route-menu-header">Route: ${net}</div>
      </div>
      <div class="route-menu-separator"></div>
      <div class="route-menu-actions">
        <div class="route-menu-item" data-action="set-net">Set Net Name...</div>
        <div class="route-menu-item" data-action="set-width">Set Width...</div>
        ${
          layerItemsHtml
            ? '<div class="route-menu-item" data-action="change-layer">Change Segment Layer...</div>'
            : ""
        }
        <div class="route-menu-item" data-action="delete">Delete Route</div>
      </div>
      ${
        layerItemsHtml
          ? `<div class="route-layer-submenu" style="display:none"><div class="route-menu-separator"></div>${layerItemsHtml}</div>`
          : ""
      }
    `;
    this.menuElement.style.display = "block";
    this.positionMenu(pageX, pageY);
    this.setupItemHandlers();
  };

  hide = (): void => {
    if (this.menuElement) this.menuElement.style.display = "none";
    this.currentRoute = null;
  };

  private setupItemHandlers = (): void => {
    if (!this.menuElement) return;

    // Main menu items
    this.menuElement
      .querySelectorAll(".route-menu-item:not(.route-layer-item)")
      .forEach((item) => {
        const el = item as HTMLElement;
        el.onclick = (e) => {
          e.stopPropagation();
          const action = el.getAttribute("data-action");
          if (action === "delete") {
            this.handleDelete();
            this.hide();
          } else if (action === "set-net") {
            if (this.currentRoute) this.editNetInline(this.currentRoute);
          } else if (action === "set-width") {
            this.handleSetWidth();
            this.hide();
          } else if (action === "change-layer") {
            // Toggle layer submenu visibility
            const submenu = this.menuElement?.querySelector(
              ".route-layer-submenu"
            ) as HTMLElement | null;
            if (submenu) {
              submenu.style.display =
                submenu.style.display === "none" ? "block" : "none";
            }
          } else {
            this.hide();
          }
        };
      });

    // Layer submenu items
    this.menuElement.querySelectorAll(".route-layer-item").forEach((item) => {
      const el = item as HTMLElement;
      el.onclick = (e) => {
        e.stopPropagation();
        const idx = parseInt(el.getAttribute("data-layer-idx") ?? "-1", 10);
        if (idx >= 0) {
          this.handleChangeSegmentLayer(idx);
        }
        this.hide();
      };
    });
  };

  private handleDelete = (): void => {
    const route = this.currentRoute;
    if (!route) return;
    const content = this.editor.content;
    if (!(content instanceof LayoutContent)) return;
    if (!content.removeEntity(route, true)) return;
    content.logChange(changes.Remove.new(route));
    content.deselect();
  };

  // Open the width dialog for the right-clicked segment. Width is per-segment,
  // so we target the clicked segment's start point (currentSegIdx) and use that
  // segment's layer for the bounds.
  private handleSetWidth = (): void => {
    const route = this.currentRoute;
    const segIdx = this.currentSegIdx;
    if (!route || segIdx === null) return;
    const content = this.editor.content;
    if (!(content instanceof LayoutContent)) return;
    const path = route.data.path;
    const routeId = route.data.id;
    // Need an in-bounds segment index and a route id to target.
    if (segIdx < 0 || segIdx >= path.length || routeId === undefined) return;
    const startPoint = path[segIdx];
    const layer = startPoint.layer;
    const { min, max, default: def } = layout.routeWidthConstraints(layer);
    this.editor.platform.notifyEditRouteWidth?.({
      currentWidth: layout.effectiveRouteWidth(startPoint.width, layer),
      minWidth: min,
      maxWidth: max,
      defaultWidth: def,
      manufacturingGridNm: content.pdk?.manufacturingGrid ?? 1,
      routeId,
      segmentIndex: segIdx,
    });
  };

  // Change the layer of the right-clicked segment only, inserting via pairs
  // at the boundaries where the new layer differs from the neighbor.
  private handleChangeSegmentLayer = (layerIdx: number): void => {
    const route = this.currentRoute;
    const segIdx = this.currentSegIdx;
    if (!route || segIdx == null) return;
    const content = this.editor.content;
    if (!(content instanceof LayoutContent)) return;
    const metals = content.stack?.metalLayers;
    if (!metals || layerIdx < 0 || layerIdx >= metals.length) return;

    const newLayer = metals[layerIdx];
    const oldPath = route.data.path;

    const sameLayer = (
      a: { layer: number; purpose: number },
      b: { layer: number; purpose: number }
    ): boolean => a.layer === b.layer && a.purpose === b.purpose;

    // Build a new path: change only the segment's two endpoints to newLayer,
    // and insert via pairs at boundaries where the layer changes.
    const newPath: typeof oldPath = [];
    for (let i = 0; i < oldPath.length; i++) {
      const rp = oldPath[i];

      if (i === segIdx) {
        // Before the segment start: insert a via if the previous point has a different layer.
        if (i > 0 && !sameLayer(oldPath[i - 1].layer, newLayer)) {
          // Via: previous layer at this position → new layer at this position
          // (previous point already in newPath with old layer)
          newPath.push({ point: { ...rp.point }, layer: newLayer });
        } else {
          newPath.push({ ...rp, point: { ...rp.point }, layer: newLayer });
        }
      } else if (i === segIdx + 1) {
        // The segment end point: set to newLayer
        newPath.push({ ...rp, point: { ...rp.point }, layer: newLayer });
        // After the segment end: insert a via if the next point has a different layer.
        if (
          i < oldPath.length - 1 &&
          !sameLayer(newLayer, oldPath[i + 1].layer)
        ) {
          // Via: new layer at this position → next segment's layer at this position
          newPath.push({ point: { ...rp.point }, layer: oldPath[i + 1].layer });
        }
      } else {
        newPath.push({ ...rp, point: { ...rp.point } });
      }
    }

    const fromPath = oldPath.map((rp) => ({ ...rp, point: { ...rp.point } }));
    route.update({ path: newPath });
    content.logChange(
      changes.update({
        target: route,
        from: { path: fromPath },
        to: { path: newPath.map((rp) => ({ ...rp, point: { ...rp.point } })) },
      })
    );
    this.editor.requestRedraw();
  };

  private setupMenuStyles = (): void => {
    if (!this.menuElement) return;
    this.applyMenuStyles();
    this.styleSheet = document.createElement("style");
    this.updateStyleSheet();
    document.head.appendChild(this.styleSheet);
  };

  private applyMenuStyles = (): void => {
    if (!this.menuElement) return;
    const p = Palette.getAll();
    const s = this.menuElement.style;
    s.position = "absolute";
    s.backgroundColor = p.background;
    s.color = p.text;
    s.border = `1px solid ${p.border}`;
    s.borderRadius = "4px";
    s.padding = "0";
    s.fontSize = "12px";
    s.fontFamily = "Monaco, 'Courier New', monospace";
    s.minWidth = "180px";
    s.zIndex = "10000";
    s.boxShadow = p.boxShadow;
    s.display = "none";
  };

  private updateStyleSheet = (): void => {
    if (!this.styleSheet) return;
    const p = Palette.getAll();
    this.styleSheet.textContent = `
      .route-menu-info { padding: 8px 12px; }
      .route-menu-header { font-weight: bold; color: ${p.textHeader}; }
      .route-menu-separator { height: 1px; background-color: ${p.border}; margin: 4px 0; }
      .route-menu-actions { padding: 4px 0; }
      .route-menu-item { padding: 6px 12px; cursor: pointer; user-select: none; }
      .route-menu-item:hover { background-color: ${p.backgroundHover}; }
    `;
  };

  updateTheme = (): void => {
    this.applyMenuStyles();
    this.updateStyleSheet();
  };

  private positionMenu = (pageX: number, pageY: number): void => {
    if (!this.menuElement) return;
    this.menuElement.style.left = `${pageX + 2}px`;
    this.menuElement.style.top = `${pageY + 2}px`;
    const rect = this.menuElement.getBoundingClientRect();
    if (rect.right > window.innerWidth)
      this.menuElement.style.left = `${Math.max(10, pageX - rect.width - 2)}px`;
    if (rect.bottom > window.innerHeight)
      this.menuElement.style.top = `${Math.max(10, pageY - rect.height - 2)}px`;
  };

  private setupGlobalClickHandler = (): void => {
    document.addEventListener("mousedown", (e) => {
      if (
        this.menuElement &&
        this.menuElement.style.display !== "none" &&
        !this.menuElement.contains(e.target as Node)
      ) {
        this.hide();
      }
    });
  };

  // Transform the header in-place: replace "(unnamed)" with an inline <input>.
  // Enter or blur commits the new net name (logged for undo); Escape cancels.
  // The "Set Net Name..." item is hidden while editing.
  private editNetInline = (route: RouteEntity): void => {
    if (!this.menuElement) return;
    const header = this.menuElement.querySelector(
      ".route-menu-header"
    ) as HTMLElement | null;
    const setNetItem = this.menuElement.querySelector(
      '[data-action="set-net"]'
    ) as HTMLElement | null;
    if (!header) return;

    const input = document.createElement("input");
    input.type = "text";
    input.value = route.data.net ?? "";
    input.placeholder = "net name";
    const p = Palette.getAll();
    input.style.cssText = [
      "background:transparent",
      "border:none",
      `border-bottom:1px solid ${p.border}`,
      `color:${p.textHeader}`,
      "font-weight:bold",
      "font-size:inherit",
      "font-family:inherit",
      "outline:none",
      "width:120px",
      "padding:0",
    ].join(";");

    // Replace header content with "Route: <input>"
    header.textContent = "Route: ";
    header.appendChild(input);
    if (setNetItem) setNetItem.style.display = "none";

    let committed = false;
    const commit = (): void => {
      if (committed) return;
      committed = true;
      const net = input.value.trim();
      const oldNet = route.data.net ?? "";
      if (net !== oldNet) {
        const content = this.editor.content;
        if (content instanceof LayoutContent) {
          route.update({ net });
          content.logChange(
            changes.update({
              target: route,
              from: { net: oldNet },
              to: { net },
            })
          );
          this.editor.requestRedraw();
        }
      }
      this.hide();
    };

    input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        commit();
        e.preventDefault();
      } else if (e.key === "Escape") {
        committed = true; // skip commit on blur
        this.hide();
        e.preventDefault();
      }
    });
    input.addEventListener("blur", commit);

    requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
  };

  dispose = (): void => {
    if (this.menuElement) {
      this.menuElement.remove();
      this.menuElement = null;
    }
    this.currentRoute = null;
  };
}
